/**
 * OTPInput.js - OTP 입력 컴포넌트
 * 각 숫자를 큰 원형 박스에 표시
 */

import React, { useRef, useEffect } from 'react';
import { Box, TextField } from '@mui/material';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

const OTPInput = ({ value, onChange, length = 6, disabled = false, error = false }) => {
  const { colors } = useCustomTheme();
  const inputRefs = useRef([]);

  useEffect(() => {
    // 첫 번째 입력 필드에 포커스
    if (inputRefs.current[0] && !value) {
      inputRefs.current[0].focus();
    }
  }, []);

  // 에러 발생 시 또는 value가 비워질 때 첫 번째 필드에 포커스
  useEffect(() => {
    if (error && !value && inputRefs.current[0]) {
      // 에러 발생 후 값이 초기화되면 첫 번째 필드에 포커스
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 50);
    }
  }, [error, value]);

  const handleChange = (index, e) => {
    const input = e.target.value;
    
    // 숫자만 허용
    if (input && !/^\d$/.test(input)) {
      return;
    }

    const newValue = value.split('');
    newValue[index] = input;
    const updatedValue = newValue.join('').slice(0, length);
    
    onChange(updatedValue);

    // 다음 입력 필드로 포커스 이동
    if (input && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Backspace 키 처리
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, length);
    if (/^\d+$/.test(pastedData)) {
      onChange(pastedData);
      // 마지막 입력 필드로 포커스 이동
      const focusIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        marginBottom: '24px',
      }}
    >
      {Array.from({ length }).map((_, index) => {
        const digit = value[index] || '';
        return (
          <Box
            key={index}
            sx={{
              width: '42px',
              height: '42px',
              minWidth: '42px',
              minHeight: '42px',
              maxWidth: '42px',
              maxHeight: '42px',
              aspectRatio: '1 / 1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              border: `2px solid ${error ? '#f44336' : colors.primaryBorder}`,
              backgroundColor: digit ? colors.accent : 'transparent',
              transition: 'all 0.2s ease',
              flexShrink: 0,
              overflow: 'hidden',
              '&:focus-within': {
                borderColor: colors.accent,
                borderWidth: '3px',
                transform: 'scale(1.08)',
                boxShadow: `0 0 0 3px ${colors.accent}20`,
              },
            }}
          >
            <TextField
              inputRef={(el) => (inputRefs.current[index] = el)}
              value={digit}
              onChange={(e) => handleChange(index, e)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={disabled}
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: 'center',
                  fontSize: '20px',
                  fontWeight: 700,
                  color: 'transparent', // 숫자 숨기기
                  padding: 0,
                  margin: 0,
                  border: 'none',
                  background: 'transparent',
                  width: '100%',
                  height: '100%',
                  caretColor: 'transparent', // 커서도 숨기기
                },
              }}
              sx={{
                width: '100%',
                height: '100%',
                padding: 0,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& .MuiOutlinedInput-root': {
                  width: '100%',
                  height: '100%',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& fieldset': {
                    border: 'none',
                    padding: 0,
                    margin: 0,
                  },
                  '&:hover fieldset': {
                    border: 'none',
                    padding: 0,
                    margin: 0,
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                    padding: 0,
                    margin: 0,
                  },
                },
                '& .MuiInputBase-input': {
                  width: '100%',
                  height: '100%',
                  padding: 0,
                  margin: 0,
                  textAlign: 'center',
                  fontSize: '20px',
                  fontWeight: 700,
                  boxSizing: 'border-box',
                  lineHeight: '42px',
                  verticalAlign: 'middle',
                  color: 'transparent', // 숫자 숨기기
                  caretColor: 'transparent', // 커서도 숨기기
                },
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default OTPInput;

